import { Component } from '@mycolorway/tao';
import $ from 'jquery';
import moment from 'moment';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import resourceSubject from 'next/javascripts/reactivity/resource-subject';
import { closest } from 'next/javascripts/modules/dom';
import TableView from '../element';
import comparators from '../comparators';
import registerRenderers from './renderers';
import { registerRowSelectPlugin } from './plugins/row-select';

export default Component('tr-project-table-view-todo-table', {
  extends: [TableView],

  mixins: [eventListenerMixin],

  properties: {
    projectGuid: String,
    groupBy: String,
    todoCreationLink: String,
    todoCreationUrl: String,
  },

  configs: {
    autoWrapCol: false,
    autoWrapRow: false,
    copyPaste: true,
    columnSorting: true,
    fixedColumnsLeft: 1,
    fillHandle: {
      autoInsertRow: false,
      direction: 'vertical',
    },
    rowHeaders: true,
    selectionMode: 'single',
    persistentState: true,
  },

  ready() {
    this.subscription = resourceSubject('todo').subscribe({
      next: this.syncTodo.bind(this),
    });
  },

  disconnected() {
    this.subscription.unsubscribe();
  },

  rerender() {
    if (this.handsontable) this.handsontable.render();
  },

  beforeInitHandsontable() {
    registerRenderers(this.Handsontable);
    registerRowSelectPlugin(this.Handsontable);
    this.registerBeforeColumnSortHook();
  },

  afterInitHandsontable() {
    this.handsontable.addHook('beforeOnCellMouseDown', this.handleBeforeOnCellMouseDown.bind(this));
    this.handsontable.addHook('beforeChangeRender', this.checkDatesValid.bind(this));
    this.handsontable.addHook('afterChange', this.handleChange.bind(this));
    this.handsontable.addHook('afterAttachmentsChange', this.handleAttachmentsChange.bind(this));
    this.handsontable.addHook('afterDocumentKeyDown', this.handleTitleCellEditorKeyDown.bind(this));
    this.handsontable.addHook('afterRowSelectChange', this.handleRowSelectChange.bind(this));
    this.handsontable.addHook('beforeRemoveRow', this.handleBeforeRemoveRow.bind(this));
    this.handsontable.addHook('beforePaste', () => false);

    this.handsontable.addHook('afterScrollHorizontally', () => {
      this.namespacedTrigger('scrollHorizontally');
    });
  },

  registerBeforeColumnSortHook() {
    const self = this;

    this.Handsontable.hooks.add('beforeColumnSort', function beforeColumnSort(...args) {
      if (this.rootElement !== self) return true;

      self.handsontable = self.handsontable || this;
      return this.rootElement.handleBeforeColumnSort(...args);
    });
  },

  clearSelectedRows() {
    this.handsontable.getPlugin('rowSelect').clearSelected();
  },

  getTotalRows() {
    const totalRows = this.handsontable.countRows();

    if (this.groupBy) {
      const groupRows = this.handsontable
        .getSourceData().filter(data => data.group).length;
      return totalRows - groupRows;
    }

    return totalRows;
  },

  getRowIds(physicalRows) {
    return physicalRows.map((physicalRow) => {
      const visualRow = this.handsontable.toVisualRow(physicalRow);
      return this.handsontable.getDataAtRowProp(visualRow, 'id');
    }).filter(id => !!id);
  },

  refreshTableSize() {
    this.handsontable.view.wt.wtOverlays.adjustElementsSize(true);
  },

  getTableScrollX() {
    if (!this.lastColumnHeader || !this.lastColumnHeader.parentElement) {
      this.lastColumnHeader = this.handsontable
        .view.THEAD.querySelector('th:nth-last-child(-n+2)');
    }

    return this.lastColumnHeader.getBoundingClientRect().right;
  },

  renderColHeader(colIndex) {
    const column = this.columns[colIndex];
    const content = [column.title];

    if (column.icon) {
      content.unshift(`<i class="twr twr-${column.icon}"></i>`);
    }

    if (column.name === 'title' && (!this.groupBy || this.groupBy === 'creator')) {
      content.push(this.renderTodoCreationButton(-1));
    }

    return content.join('');
  },

  beforeGroupCellRender(group) {
    if (this.groupBy !== 'creator') {
      group.insertAdjacentHTML('beforeend', `
        <div class="action">${this.renderTodoCreationButton(group.dataset.row)}</div>
      `);
    }

    return group;
  },

  renderTodoCreationButton(row) {
    return this.todoCreationLink
      ? $(this.todoCreationLink).attr('data-row', row).get(0).outerHTML : '';
  },

  handleRowSelectChange(rows) {
    this.namespacedTrigger('selectedRowsChange', { detail: this.getRowIds(rows) });
  },

  handleBeforeRemoveRow(index, amount, physicalRows) {
    this.namespacedTrigger('removeRows', { detail: this.getRowIds(physicalRows) });
  },

  handleBeforeOnCellMouseDown(event, coords, td, controller) {
    if (closest(event.target, '.columnSorting.sortAction')) {
      // eslint-disable-next-line no-param-reassign
      controller.column = true;
      return;
    }

    this.handleTodoCreationLinkClick(event);
  },

  handleTitleCellEditorKeyDown(event) {
    const isEscape = event.key === 'Escape' || event.keyCode === 27;
    if (!isEscape) return;

    const activeEditor = this.handsontable.getActiveEditor();
    if (activeEditor && activeEditor.prop === 'title') {
      if (activeEditor.state === 'STATE_EDITING') return;
      if (this.handsontable.getDataAtRowProp(activeEditor.row, 'id')) return;

      this.handsontable.alter('remove_row', activeEditor.row);
      event.stopImmediatePropagation();
    }
  },

  handleBeforeColumnSort(currentSortConfig, destinationSortConfigs) {
    if (!this.groupBy) return true;

    const columnSortPlugin = this.handsontable.getPlugin('columnSorting');
    columnSortPlugin.setSortConfig(destinationSortConfigs);
    columnSortPlugin.saveAllSortSettings();

    if (destinationSortConfigs.length === 0) {
      this.handsontable.loadData(this.data.slice(0));
      return false;
    }

    const [{ column, sortOrder }] = destinationSortConfigs;
    this.sortColumnWithGroup(column, sortOrder);

    return false;
  },

  sortColumnWithGroup(column, sortOrder) {
    const data = {};
    let lastGroup = null;

    this.handsontable.getSourceData().slice(0).forEach((item) => {
      if (item.group) {
        lastGroup = item;
        data[lastGroup.groupValue] = data[lastGroup.groupValue] || [];
        data[lastGroup.groupValue].push(item);
      } else if (lastGroup) {
        data[lastGroup.groupValue].push(item);
      }
    });

    const columnMeta = this.handsontable.getSettings().columns[column];
    const compareFunction = this.columnSortComparator(sortOrder, columnMeta);
    const sortedData = [];

    Object.keys(data).forEach((key) => {
      const items = data[key].slice(1);
      this.Handsontable.utils.mergeSort(items, compareFunction);

      sortedData.push(data[key][0], ...items);
    });

    this.handsontable.loadData(sortedData);
  },

  columnSortComparator(sortOrder, columnMeta) {
    const compareFunctionFactory = comparators[columnMeta.renderer] || comparators.default;
    const compareFunction = compareFunctionFactory(sortOrder);

    return (originalValue, nextOriginalValue) => {
      const value = originalValue[columnMeta.name];
      const nextValue = nextOriginalValue[columnMeta.name];
      return compareFunction(value, nextValue);
    };
  },

  syncTodo(event, { id, rowData, isSubTodo = {} }) {
    const physicalRow = this.handsontable.getSourceData()
      .findIndex(data => data.id === id);
    const visualRow = this.handsontable.toVisualRow(physicalRow);

    if (event === 'update' || event === 'move') {
      if (visualRow === -1) return;

      if (event === 'move' && (rowData.projectGuid !== this.projectGuid || isSubTodo === true)) {
        this.handsontable.alter('remove_row', visualRow, 1);
      } else {
        const data = Object.keys(rowData)
          .map(prop => [visualRow, prop, rowData[prop]]);
        this.handsontable.setDataAtRowProp(data, 'syncTodo');
      }
    } else if (event === 'destroy') {
      const page = document.querySelector('.tr-page');
      if (page && page.opened) page.close();

      if (visualRow !== -1) this.handsontable.alter('remove_row', visualRow, 1);
    }
  },

  handleTodoCreationLinkClick(event) {
    const todoCreationLink = closest(event.target, '.link-todo-creation');

    if (todoCreationLink) {
      event.stopImmediatePropagation();

      const params = {};
      const parentRowIndex = parseInt(todoCreationLink.dataset.row, 10);

      if (this.groupBy) {
        const groupValue = this.handsontable.getDataAtRowProp(parentRowIndex, 'groupValue');

        if (this.groupBy === 'todolist') {
          params.todolist_guid = groupValue;
        } else if (this.groupBy === 'assignee') {
          params.todos_creation = { assignee_guid: groupValue };
        } else if (this.groupBy === 'priority') {
          params.todos_creation = { priority: groupValue };
        }
      }

      params.todolist_guid = params.todolist_guid || this.getDefaultTodolistForNewTodo();
      $(todoCreationLink).data('params', $.param(params))
        .one('ajax:success', (e, { row: todo, creatorGroupRow: creatorGroup }) => {
          this.insertNewTodoRow(parentRowIndex, todo, creatorGroup);
          if (todo.todolist) this.updateTodolistChoices(JSON.parse(todo.todolist));
        });
    }
  },

  getDefaultTodolistForNewTodo() {
    const { choices } = this.handsontable
      .getSettings().columns.find(col => col.name === 'todolist');
    if (!choices || choices.length === 0) return '';

    return choices[0][1];
  },

  insertNewTodoRow(parentRowIndex, todo, creatorGroup) {
    let rowIndex = this.handsontable.toVisualRow(parentRowIndex + 1);

    if (this.groupBy === 'creator') {
      const groupRowIndex = this.handsontable.getSourceData()
        .findIndex(data => data.group && data.groupValue === creatorGroup.groupValue);

      if (groupRowIndex === -1) {
        this.insertCreatorGroupRowAtTop(creatorGroup);
        rowIndex = 1;
      } else {
        rowIndex = this.handsontable.toVisualRow(groupRowIndex + 1);
      }
    }

    this.handsontable.alter('insert_row', rowIndex, 1);

    const rowProps = Object.keys(todo).map(prop => [rowIndex, prop, todo[prop]]);
    this.handsontable.setDataAtRowProp(rowProps, 'insertRow');
    this.handsontable.selectCell(rowIndex, 0);

    const editor = this.handsontable.getActiveEditor();
    editor.enableFullEditMode();
    editor.beginEditing();
  },

  updateTodolistChoices(todolist) {
    const { choices } = this.handsontable
      .getSettings().columns.find(col => col.name === 'todolist');

    if (choices.findIndex(item => item.value === todolist.value) === -1) {
      choices.push([
        todolist.text, todolist.value,
        Object.keys(todolist.data).reduce((data, key) => {
          // eslint-disable-next-line no-param-reassign
          data[`data-${key}`] = todolist.data[key];
          return data;
        }, {}),
      ]);
    }
  },

  insertCreatorGroupRowAtTop(creatorGroup) {
    this.handsontable.alter('insert_row', 0, 1);

    const rowProps = Object.keys(creatorGroup).map(prop => [0, prop, creatorGroup[prop]]);
    this.handsontable.setDataAtRowProp(rowProps, 'insertRow');
    this.handsontable.getPlugin('mergeCells')
      .merge(0, 0, 0, this.handsontable.countCols() - 1);
  },

  checkDatesValid(changes, source) {
    if (source === 'fromDueDate' || source === 'fromStartDate') return;

    changes.forEach(([row, prop, oldVal, newVal]) => {
      if (oldVal === newVal || !newVal) return;

      if (prop === 'start_date') {
        const dueDate = this.handsontable.getDataAtRowProp(row, 'due_date');
        if (dueDate && moment(newVal) > moment(dueDate)) {
          const col = this.handsontable.propToCol('start_date');
          this.handsontable.setCellMetaObject(row, col, {
            valid: false,
            errorMessage: '开始时间不能晚于截止时间',
          });
        }
      } else if (prop === 'due_date') {
        const startDate = this.handsontable.getDataAtRowProp(row, 'start_date');
        if (startDate && moment(newVal) < moment(startDate)) {
          const col = this.handsontable.propToCol('due_date');
          this.handsontable.setCellMetaObject(row, col, {
            valid: false,
            errorMessage: '截止时间不能早于开始时间',
          });
        }
      }
    });
  },

  handleChange(changes, source) {
    if ((source !== 'edit' && source !== 'Autofill.fill') || source === 'syncTodo') return;

    const propNames = this.handsontable.getSettings().columns.map(col => col.name);

    changes.forEach(([row, prop, oldVal, newVal]) => {
      const isGroupRow = this.handsontable.getDataAtRowProp(row, 'group');
      if (isGroupRow || oldVal === newVal || !propNames.includes(prop)) return;

      const todoId = this.handsontable.getDataAtRowProp(row, 'id');
      if (todoId) {
        this.handleTodoChange(row, prop, newVal, source);
      } else if (!this.todoCreating) {
        this.createTodo(row);
      }
    });
  },

  createTodo(row) {
    const title = this.handsontable.getDataAtRowProp(row, 'title');
    if (!title) return;

    const todolist = this.handsontable.getDataAtRowProp(row, 'todolist');
    if (!todolist) return;

    this.todoCreating = true;

    $.ajax({
      url: this.todoCreationUrl,
      method: 'post',
      data: {
        todolist_guid: JSON.parse(todolist).value,
        todo: {
          title,
          ...this.buildNewTodoData(row),
        },
      },
      dataType: 'json',
    }).then(({ row: todo }) => {
      this.todoCreating = false;

      const rowProps = [];
      Object.keys(todo).forEach((prop) => {
        rowProps.push([row, prop, todo[prop]]);
      });

      this.handsontable.setDataAtRowProp(rowProps, 'insertRow');
    });
  },

  buildNewTodoData(row) {
    const data = {};

    const dueDate = this.handsontable.getDataAtRowProp(row, 'due_date');
    if (dueDate) {
      Object.assign(data, this.transformDueDate(row, dueDate));
    }

    const excoludeProps = ['start_date', 'due_date', 'todolist'];
    this.columns.filter(
      column => excoludeProps.indexOf(column.data) === -1 && !column.readOnly,
    ).forEach(({ data: prop }) => {
      const value = this.handsontable.getDataAtRowProp(row, prop);
      if (value) Object.assign(data, this.transformCellValue(row, prop, value));
    });

    return data;
  },

  handleTodoChange(row, prop, newVal, source) {
    const params = {};

    switch (prop) {
      case 'is_completed':
        this.toggleTodoCompleted(row, newVal);
        return;
      case 'todolist':
        this.updateTodolist(row, newVal ? JSON.parse(newVal).value : null);
        return;
      case 'start_date':
        if (source !== 'fromDueDate') {
          Object.assign(params, this.transformStartDate(row, newVal));
        }
        break;
      case 'due_date':
        if (source !== 'fromStartDate') {
          Object.assign(params, this.transformDueDate(row, newVal));
        }
        break;
      default:
        Object.assign(params, this.transformCellValue(row, prop, newVal));
        break;
    }

    if (Object.keys(params).length > 0) this.updateTodo(row, params);
  },

  handleAttachmentsChange(row, prop, attfileIds) {
    this.updateTodo(row, { [prop]: attfileIds });
  },

  transformStartDate(row, value) {
    if (!value) return { start_date: null };

    const dueDate = this.handsontable.getDataAtRowProp(row, 'due_date');

    if (!dueDate) {
      this.handsontable.setDataAtRowProp(row, 'due_date', value, 'fromStartDate');
      return { start_date: value, due_date: value };
    }

    if (moment(value) <= moment(dueDate)) return { start_date: value };

    return {};
  },

  transformDueDate(row, value) {
    if (!value) {
      this.handsontable.setDataAtRowProp(row, 'start_date', '', 'fromDueDate');
      return { start_date: null, due_date: null };
    }

    const startDate = this.handsontable.getDataAtRowProp(row, 'start_date');
    if (!startDate || moment(value) >= moment(startDate)) {
      return { start_date: startDate, due_date: value };
    }

    return {};
  },

  transformCellValue(row, prop, value) {
    const col = this.getColumnByProp(prop);
    const cellMeta = this.handsontable.getCellMeta(row, col);
    let propValue = value;

    switch (cellMeta.editor) {
      case 'select':
        propValue = this.transformSelectCellValue(row, col, value);
        if (cellMeta.multiple && propValue.length === 0) {
          return { [`${prop}[]`]: '' };
        }
        return { [prop]: propValue };
      case 'attachment':
        propValue = this.transformAttachmentCellValue(row, col, value);
        return propValue.length > 0 ? { [prop]: propValue } : { [`${prop}[]`]: '' };
      default:
        return { [prop]: propValue };
    }
  },

  transformSelectCellValue(row, col, value) {
    const { multiple } = this.handsontable.getCellMeta(row, col);
    if (!value) return multiple ? [] : null;

    if (multiple) {
      return JSON.parse(value).map(item => item.value);
    }

    return JSON.parse(value).value;
  },

  transformAttachmentCellValue(row, col, value) {
    const attachments = value ? JSON.parse(value) : [];
    return attachments.map(attachment => attachment.attfileGuid);
  },

  updateTodo(row, params) {
    const url = this.handsontable.getDataAtRowProp(row, 'editUrl');
    $.ajax({
      url,
      type: 'patch',
      dataType: 'json',
      data: { todo: params },
    });
  },

  toggleTodoCompleted(row, completed = false) {
    const url = this.handsontable.getDataAtRowProp(
      row, completed ? 'completeUrl' : 'reopenUrl',
    );

    $.ajax({
      url,
      type: 'post',
      dataType: 'script',
    });
  },

  updateTodolist(row, todolistGuid) {
    const url = this.handsontable.getDataAtRowProp(row, 'reorderUrl');
    const completed = this.handsontable.getDataAtRowProp(row, 'is_completed');

    $.ajax({
      url,
      method: 'post',
      data: {
        list_guid: todolistGuid,
        completed,
      },
    });
  },
});
